/*
  re.loved.by.shahd — motion runtime (vanilla port of the React motion components)
  - Scroll reveals via IntersectionObserver (.reveal, .reveal-clip, .stagger, .mask-i, .img-reveal)
  - Infinite marquee (duplicates track for seamless loop)
  - Parallax (transform only)
  - Counter (animated stat numbers)
  - Custom cursor (fine pointer only)
  All respect prefers-reduced-motion.
*/
(function () {
  'use strict';

  // Site is set to always animate (owner preference), so motion runs
  // regardless of the OS reduce-motion setting. The custom cursor still
  // only activates for fine pointers below.
  var reduce = false;

  /* ── Scroll reveals ─────────────────────────────────────────── */
  function initReveals() {
    var targets = document.querySelectorAll('.reveal, .reveal-clip, .stagger, [data-mask], [data-img-reveal]');
    if (reduce || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.01, rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ── Marquee ────────────────────────────────────────────────── */
  function initMarquees() {
    document.querySelectorAll('[data-marquee]').forEach(function (track) {
      if (track.dataset.marqueeReady) return;
      track.dataset.marqueeReady = '1';
      var dur = track.getAttribute('data-marquee') || '30';
      track.style.setProperty('--marquee-duration', dur + 's');
      // duplicate content so translateX(-50%) loops seamlessly
      track.innerHTML = track.innerHTML + track.innerHTML;
    });
  }

  /* ── Parallax ───────────────────────────────────────────────── */
  function initParallax() {
    if (reduce) return;
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (!items.length) return;
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.15;
        var rect = el.getBoundingClientRect();
        var center = rect.top + rect.height / 2;
        var offset = (center - vh / 2) * -speed;
        el.style.setProperty('--py', offset.toFixed(1) + 'px');
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ── Counter ────────────────────────────────────────────────── */
  function initCounters() {
    var els = document.querySelectorAll('[data-counter]');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.textContent = (el.getAttribute('data-prefix') || '') + el.getAttribute('data-counter') + (el.getAttribute('data-suffix') || '');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        io.unobserve(el);
        var target = parseFloat(el.getAttribute('data-counter')) || 0;
        var prefix = el.getAttribute('data-prefix') || '';
        var suffix = el.getAttribute('data-suffix') || '';
        var dur = 1600, start = null;
        function step(ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = prefix + Math.round(target * eased) + suffix;
          if (p < 1) window.requestAnimationFrame(step);
        }
        window.requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ── Custom cursor (fine pointer only) — simplified per owner feedback ── */
  function initCursor() {
    if (reduce) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var dot = document.createElement('div');
    dot.className = 'cursor-dot';
    document.body.appendChild(dot);
    document.documentElement.classList.add('has-custom-cursor');

    var tx = -100, ty = -100;
    document.addEventListener('mousemove', function (e) {
      tx = e.clientX; ty = e.clientY;
      dot.style.setProperty('--dx', tx + 'px');
      dot.style.setProperty('--dy', ty + 'px');
    });

    var interactive = 'a, button, [role="button"], input, textarea, select, label';
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest(interactive)) dot.setAttribute('data-variant', 'link');
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest(interactive)) dot.removeAttribute('data-variant');
    });
    document.addEventListener('mouseleave', function () { dot.setAttribute('data-variant', 'hidden'); });
    document.addEventListener('mouseenter', function () { dot.removeAttribute('data-variant'); });
  }

  /* ── Sticky header on scroll ────────────────────────────────── */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    function onScroll() {
      if (window.scrollY > 12) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function init() {
    initReveals();
    initMarquees();
    initParallax();
    initCounters();
    initCursor();
    initHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init reveals/marquees when Shopify theme editor injects sections
  document.addEventListener('shopify:section:load', function () {
    initReveals();
    initMarquees();
    initParallax();
    initCounters();
  });
})();
